import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Share2, Calendar } from "lucide-react";
import axios from "axios";
import API_URL from "../config";
import DOMPurify from "dompurify";
import Cta from "../blog/Cta";
import { Helmet } from "react-helmet";

export default function SingleBlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch main post
        const { data: postData } = await axios.get(
          `${API_URL}/api/blogs/${slug}`
        );
        setPost(postData);

        // Build related params safely
        const params = {
          exclude: slug,
          limit: 3,
        };

        if (Array.isArray(postData.categories) && postData.categories.length) {
          params.categories = postData.categories.join(",");
        } else if (postData.categories) {
          params.categories = postData.categories;
        }

        const { data: relatedData } = await axios.get(
          `${API_URL}/api/blogs`,
          { params }
        );

        setRelatedPosts(relatedData.posts || relatedData || []);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load article"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Share function
  const shareUrl = (platform) => {
    if (!post) return "#";
    const url = encodeURIComponent(
      `https://www.frostandgrey.com/blog/${post.slug}`
    );
    const text = encodeURIComponent(post.title);
    switch (platform) {
      case "facebook":
        return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
      case "x":
        return `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
      case "linkedin":
        return `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`;
      default:
        return "#";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen">
        <div className="flex justify-center items-center py-24 px-6">
          <div className="text-center">
            <div className="w-px h-10 sm:h-12 bg-gradient-to-b from-transparent via-white to-transparent mx-auto mb-4" />
            <p className="text-gray-400 tracking-[0.2em] uppercase text-xs sm:text-sm">
              Loading Article
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error / not found state
  if (error || !post) {
    return (
      <div className="bg-black text-white min-h-screen">
        <div className="max-w-4xl mx-auto py-16 sm:py-20 lg:py-24 px-6 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.1em] uppercase text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
          <div className="py-16 sm:py-20">
            <h1 className="text-3xl sm:text-4xl font-thin tracking-tight leading-tight mb-4 sm:mb-6">
              Article Not Found
            </h1>
            <p className="text-gray-400 text-sm sm:text-lg font-light tracking-wide mb-8 max-w-xl mx-auto">
              The article you are looking for does not exist, has been moved, or
              is temporarily unavailable.
            </p>
            <Link
              to="/blog"
              className="inline-block border border-white/20 hover:border-white/40 px-8 py-3 text-xs sm:text-sm tracking-[0.1em] uppercase font-light text-white hover:bg-white/5 transition-all duration-300"
            >
              Browse Articles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const categories = Array.isArray(post.categories)
    ? post.categories
    : post.categories
    ? [post.categories]
    : [];

  const metaDescription =
    post.excerpt ||
    "AI insights, governance, training, and business transformation across Africa.";

  return (
    <div className="bg-black text-white min-h-screen">
      <Helmet>
        <title>{post.title} | Frost & Grey</title>

        <meta name="description" content={metaDescription} />

        <link
          rel="canonical"
          href={`https://frostandgrey.com/blog/${post.slug}`}
        />

        <meta property="og:title" content={`${post.title} | Frost & Grey`} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:url"
          content={`https://frostandgrey.com/blog/${post.slug}`}
        />
        <meta property="og:type" content="article" />
        {post.image && <meta property="og:image" content={post.image} />}
      </Helmet>

      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 md:h-[420px] lg:h-[500px]">
        {post.image && (
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      </div>

      {/* Article Card */}
      <div className="max-w-5xl mx-auto px-6 -mt-20 sm:-mt-24 lg:-mt-28 relative z-10 pb-16 sm:pb-20 lg:pb-24">
        {/* Back link */}
      {/* <div className="max-w-7xl mx-auto px-6 pt-8 sm:pt-10 lg:pt-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.1em] uppercase text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </Link>
      </div> */}
        <article className="bg-black/80 border border-gray-800 p-6 sm:p-8 md:p-10 lg:p-12 backdrop-blur-sm">
          {/* Categories */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((category) => (
                <span
                  key={category}
                  className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-gray-300 border-b border-gray-600 pb-1"
                >
                  {category}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-thin tracking-tight leading-tight mb-6 sm:mb-8">
            {post.title}
          </h1>

          {/* Meta + Share */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-8 pb-8 border-b border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Calendar size={16} />
              <time className="tracking-wide">
                {new Date(post.createdAt || post.date).toLocaleDateString(
                  "en-US",
                  {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  }
                )}
              </time>
            </div>

            <div className="flex items-center gap-2 sm:ml-auto">
              <Share2 size={16} className="text-gray-500" />
              <span className="text-xs text-gray-500 tracking-[0.15em] uppercase">
                Share
              </span>
              <div className="flex items-center gap-1">
                <a
                  href={shareUrl("facebook")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                  </svg>
                </a>
                <a
                  href={shareUrl("x")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 300 300"
                  >
                    <path d="M178.57 127.15 290.27 0h-26.46l-97.03 110.38L89.34 0H0l117.13 166.93L0 300.25h26.46l102.4-116.59 81.8 116.59h89.34M36.01 19.54H76.66l187.13 262.13h-40.66" />
                  </svg>
                </a>
                <a
                  href={shareUrl("linkedin")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Article body */}
          <div
            className="prose prose-invert prose-lg max-w-none text-gray-200"
            style={{
              "--tw-prose-body": "rgba(229, 231, 235, 0.85)",
              "--tw-prose-headings": "rgba(255, 255, 255, 1)",
              "--tw-prose-links": "rgba(156, 163, 175, 1)",
              "--tw-prose-bold": "rgba(255, 255, 255, 1)",
              "--tw-prose-counters": "rgba(156, 163, 175, 1)",
              "--tw-prose-bullets": "rgba(75, 85, 99, 1)",
              "--tw-prose-hr": "rgba(55, 65, 81, 1)",
              "--tw-prose-quotes": "rgba(229, 231, 235, 1)",
              "--tw-prose-quote-borders": "rgba(75, 85, 99, 1)",
              "--tw-prose-captions": "rgba(156, 163, 175, 1)",
              "--tw-prose-code": "rgba(229, 231, 235, 1)",
              "--tw-prose-pre-code": "rgba(229, 231, 235, 1)",
              "--tw-prose-pre-bg": "rgba(17, 24, 39, 1)",
              "--tw-prose-th-borders": "rgba(75, 85, 99, 1)",
              "--tw-prose-td-borders": "rgba(55, 65, 81, 1)",
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(post.content || ""),
            }}
          />
        </article>
      </div>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pb-20 sm:pb-24 lg:pb-28">
          <div className="text-center mb-12 sm:mb-16">
            <div className="w-px h-10 sm:h-12 bg-gradient-to-b from-transparent via-white to-transparent mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-thin tracking-tight mb-3 text-white">
              Related Articles
            </h2>
            <p className="text-sm sm:text-base text-gray-400 font-light">
              Continue exploring our AI insights and perspectives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {relatedPosts.map((relatedPost) => {
              const relatedCategories = Array.isArray(relatedPost.categories)
                ? relatedPost.categories
                : relatedPost.categories
                ? [relatedPost.categories]
                : [];

              return (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <article className="cursor-pointer">
                    <div className="aspect-[16/10] overflow-hidden mb-4 rounded-lg">
                      {relatedPost.image && (
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      )}
                    </div>
                    {relatedCategories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {relatedCategories.slice(0, 2).map((category, i, arr) => (
                          <span
                            key={category}
                            className="text-[10px] sm:text-xs tracking-[0.2em] text-gray-400 border-b border-gray-700 pb-1 flex items-center"
                          >
                            {category}
                            {i < arr.length - 1 && (
                              <span className="mx-1 text-gray-500 text-xs">
                                •
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    <h3 className="text-md sm:text-lg font-thin leading-snug mb-3 text-gray-200 group-hover:text-white transition-colors duration-300">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4 font-light line-clamp-3">
                      {DOMPurify.sanitize(
                        (relatedPost.excerpt ||
                          relatedPost.content ||
                          ""
                        ).replace(/<[^>]+>/g, "")
                      ).slice(0, 160)}
                      {relatedPost.content &&
                        relatedPost.content.length > 160 &&
                        "…"}
                    </p>
                    <time className="text-[10px] sm:text-xs tracking-[0.2em] text-gray-500">
                      {new Date(
                        relatedPost.createdAt || relatedPost.date
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </time>
                  </article>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Global blog CTA */}
      <Cta />
    </div>
  );
}
